import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import EmptyStateText from '../../components/EmptyState';
import ExpansionPanel from '../../components/ExpansionPanel';
import Card from '../../components/Card';
import PageTitle from '../../components/PageTitle';
import FloatingButton from '~/components/FloatingButton';
import { DotsVertIcon, EditButton } from '~/components/EditButton/styles';

import { getBlocksByWorkoutId, getWorkoutById, deleteExercise } from '~/api';

import EditMenu from './EditMenu';
import NewBlockDialog from './NewBlockDialog';
import EditExerciseDialog from './EditExerciseDialog';
import Loading from '~/components/Loading';

export default function Workout({ match, location }) {
  const {
    params: { workoutId },
  } = match;
  const { state: workoutState } = location;

  const user = useSelector((state) => state.user.user);
  const isAdmin = user.role === 'ADMIN';

  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState();
  const [workout, setWorkout] = useState(workoutState);
  const [blocks, setBlocks] = useState([]);
  const [blockToEdit, setBlockToEdit] = useState();
  const [newBlockDialogIsOpen, setNewBlockDialogIsOpen] = useState(false);
  const [exerciseToEdit, setExerciseToEdit] = useState();
  const [editExerciseDialogIsOpen, setEditExerciseDialogIsOpen] = useState(
    false
  );

  // Handle the edit menu from each block card
  const [anchorEl, setAnchorEl] = useState(null);
  const editMenuIsOpen = !!anchorEl;

  // const hasExercises = blocks.some((block) => block.exercises?.length > 0);
  const hasExercises = blocks.length > 0;

  const getWorkout = async () => {
    const { data: workoutResponse } = await getWorkoutById(workoutId);
    setWorkout(workoutResponse);
  };

  const getBlocks = async () => {
    try {
      const { data: blocksResponse } = await getBlocksByWorkoutId(workoutId);
      setBlocks(blocksResponse);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!workoutState) {
      getWorkout();
    }
    getBlocks();
  }, []);

  const handleChange = (id) => (_event, newExpanded) => {
    setExpanded(newExpanded ? id : false);
  };

  const handleOpenMenu = (event, block) => {
    setAnchorEl(event.currentTarget);
    setBlockToEdit(block);
  };

  const handleDeleteExercise = async (exerciseId) => {
    try {
      await deleteExercise(exerciseId);
      toast.success('Exercício deletado com sucesso!');

      // Refresh the page
      getBlocks();
    } catch (error) {
      toast.error('Falha ao deletar exercício!');
    }
  };

  const handleEditExercise = async (exercise) => {
    setExerciseToEdit(exercise);
    setEditExerciseDialogIsOpen(true);
  };

  return (
    <>
      {workout ? <PageTitle>{workout.name}</PageTitle> : null}
      {isLoading && <Loading />}
      {!isLoading &&
        hasExercises &&
        blocks.map((block) => {
          const blockExercises = block.exercises;

          return (
            <Card key={block.id} backgroundColor="rgb(240, 240, 240, 0.5)">
              <>
                <h2 style={{ color: '#272727', margin: '15px' }}>
                  {block.name}
                </h2>

                {isAdmin && (
                  <EditButton onClick={(event) => handleOpenMenu(event, block)}>
                    <DotsVertIcon color="black" />
                  </EditButton>
                )}

                {blockExercises.map((exercise) => {
                  return (
                    <ExpansionPanel
                      exercise={exercise}
                      key={exercise.id}
                      expanded={expanded === exercise.id}
                      onChange={handleChange(exercise.id)}
                      isAdmin={isAdmin}
                      onEdit={() => handleEditExercise(exercise)}
                      onDelete={handleDeleteExercise}
                    />
                  );
                })}
              </>
            </Card>
          );
        })}

      {!isLoading && !hasExercises && (
        <EmptyStateText text="Não há exercícios cadastrados nesta ficha ainda!" />
      )}

      {blockToEdit && (
        <EditMenu
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          isOpen={editMenuIsOpen}
          block={blockToEdit}
          refreshBlocks={getBlocks}
        />
      )}

      {exerciseToEdit && (
        <EditExerciseDialog
          isOpen={editExerciseDialogIsOpen}
          setIsOpen={setEditExerciseDialogIsOpen}
          exercise={exerciseToEdit}
          refreshBlocks={getBlocks}
          isAdmin={isAdmin}
        />
      )}

      {isAdmin && (
        <>
          <FloatingButton onClick={() => setNewBlockDialogIsOpen(true)} />
          <NewBlockDialog
            isOpen={newBlockDialogIsOpen}
            setIsOpen={setNewBlockDialogIsOpen}
            workoutId={workoutId}
            orderedIndex={blocks.length}
            refreshBlocks={getBlocks}
          />
        </>
      )}
    </>
  );
}

Workout.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      workoutId: PropTypes.number,
    }),
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      workoutState: PropTypes.object,
    }),
  }).isRequired,
};
